import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Package, Truck, DollarSign, AlertCircle, CheckCircle, X } from 'lucide-react';
import { colors } from '../constants/colors';
import { getNotifications, getUnreadNotificationCount, markNotificationAsRead, markAllNotificationsAsRead } from '../api/client';
import type { Notification, NotificationType } from '../types';

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'AUCTION_WON':
    case 'BIN_PURCHASED':
    case 'SALE_CONFIRMED':
      return <Package size={18} color={colors.brand.blue} />;
    case 'PAYMENT_FAILED':
      return <AlertCircle size={18} color={colors.accent.pink} />;
    case 'REFUND_ISSUED':
      return <DollarSign size={18} color={colors.accent.purple} />;
    case 'ORDER_CONFIRMED':
    case 'ORDER_CREATED':
    case 'TRACKING_ADDED':
    case 'ORDER_SHIPPED':
    case 'ORDER_DELIVERED':
      return <Truck size={18} color={colors.brand.blue} />;
    case 'PAYOUT_INITIATED':
    case 'PAYOUT_COMPLETED':
      return <CheckCircle size={18} color="#34C759" />;
    case 'PAYOUT_FAILED':
      return <AlertCircle size={18} color={colors.accent.pink} />;
    default:
      return <Bell size={18} color={colors.gray[600]} />;
  }
}

function getNotificationRoute(type: NotificationType): string {
  switch (type) {
    case 'PAYOUT_INITIATED':
    case 'PAYOUT_COMPLETED':
    case 'PAYOUT_FAILED':
      return '/sell/payouts';
    default:
      return '/orders';
  }
}

function formatTimeAgo(dateString: string): string {
  const normalized = dateString.endsWith('Z') ? dateString : dateString + 'Z';
  const date = new Date(normalized);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await getUnreadNotificationCount();
      setUnreadCount(count);
    } catch {
      // Silently fail for polling
    }
  }, []);

  // Poll unread count every 30s + on visibility change
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      const page = await getNotifications(0, 20);
      setNotifications(page.content);
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      loadNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await markNotificationAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {
        // Continue with navigation anyway
      }
    }
    setIsOpen(false);
    navigate(getNotificationRoute(notification.type));
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  };

  return (
    <div style={styles.container} ref={dropdownRef}>
      <button onClick={handleToggle} style={styles.bellButton}>
        <Bell size={20} color={colors.gray[700]} />
        {unreadCount > 0 && (
          <span style={styles.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownHeader}>
            <span style={styles.dropdownTitle}>Notifications</span>
            <div style={styles.headerActions}>
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} style={styles.markAllButton}>
                  Mark all read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} style={styles.closeButton}>
                <X size={16} color={colors.gray[600]} />
              </button>
            </div>
          </div>

          <div style={styles.notificationList}>
            {isLoading ? (
              <div style={styles.emptyState}>Loading...</div>
            ) : notifications.length === 0 ? (
              <div style={styles.emptyState}>No notifications yet</div>
            ) : (
              notifications.map(notification => (
                <button
                  key={notification.id}
                  style={{
                    ...styles.notificationItem,
                    backgroundColor: notification.read ? 'transparent' : colors.brand.blue08,
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div style={styles.notificationIcon}>
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div style={styles.notificationContent}>
                    <div style={styles.notificationHeader}>
                      <span style={{
                        ...styles.notificationTitle,
                        fontWeight: notification.read ? '400' : '600',
                      }}>
                        {notification.title}
                      </span>
                      <span style={styles.notificationTime}>
                        {formatTimeAgo(notification.createdAt)}
                      </span>
                    </div>
                    <span style={styles.notificationMessage}>{notification.message}</span>
                  </div>
                  {!notification.read && <div style={styles.unreadDot} />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
  },
  bellButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    backgroundColor: colors.accent.pink,
    color: 'white',
    fontSize: '10px',
    fontWeight: '700',
    minWidth: '16px',
    height: '16px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: '0',
    marginTop: '8px',
    width: '380px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    border: `1px solid ${colors.gray[200]}`,
    zIndex: 1000,
    overflow: 'hidden',
  },
  dropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  dropdownTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: colors.ink.black,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  markAllButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: colors.brand.blue,
    fontWeight: '500',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
  },
  notificationList: {
    maxHeight: '400px',
    overflowY: 'auto' as const,
  },
  notificationItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 16px',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
    borderBottom: `1px solid ${colors.gray[100]}`,
    transition: 'background-color 0.15s',
  },
  notificationIcon: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '2px',
  },
  notificationContent: {
    flex: 1,
    minWidth: 0,
  },
  notificationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  notificationTitle: {
    fontSize: '13px',
    color: colors.ink.black,
  },
  notificationTime: {
    fontSize: '11px',
    color: colors.gray[600],
    flexShrink: 0,
  },
  notificationMessage: {
    fontSize: '12px',
    color: colors.gray[600],
    lineHeight: '1.4',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    marginTop: '2px',
  },
  unreadDot: {
    flexShrink: 0,
    width: '8px',
    height: '8px',
    borderRadius: '4px',
    backgroundColor: colors.brand.blue,
    marginTop: '6px',
  },
  emptyState: {
    padding: '32px 16px',
    textAlign: 'center' as const,
    fontSize: '13px',
    color: colors.gray[600],
  },
};
